import tenantPayloads from '@payloads';
import tenantTransformers from '@transformers';
import tenantConstants from '@constants';
import tenantUtils from '@utils';
import { getDateLabels } from '../../../utility/date';
import { mapQueryParams } from '../../../utility/utility';

const LeadsEndpoints = {
  getLeads: {
    query: (queryParams) => {
      const { params, filtersList } = queryParams;
      const mappedParams = mapQueryParams(params, filtersList);
      return `/api/lms/leads?${mappedParams}`;
    },
    transformer: (response) => tenantTransformers.getLeadsMapper(response),
  },

  getTasksTypes: {
    query: () => `/api/task_types?search_class=Lead`,
    transformer: (response) => response?.tasks,
  },

  getTasksSubTypes: {
    query: (params) => `/api/task_purposes?q[task_type_id_eq]=${params?.taskId}&q[category_eq]=${params?.categoryId}`,
    transformer: (response) => response?.task_purposes,
  },
  addLeadInterests: {
    query: (body) => ({
      url: `/api/lms/interests`,
      method: 'POST',
      body: tenantPayloads.addLeadInterestsPayload(body),
    }),
  },
  updateLeadInterest: {
    query: (params) => ({
      url: `/api/lms/interests/${params?.interestId}`,
      method: 'PUT',
      body: params?.body,
    }),
  },
  addTask: {
    query: (body) => ({
      url: `/api/lms/tasks`,
      method: 'POST',
      body: tenantPayloads.addTaskPayload(body),
    }),
  },
  updateLeadName: {
    query: (params) => ({
      url: `/api/lms/leads/${params?.leadId}`,
      method: 'PUT',
      body: tenantPayloads.updateLeadNamePayload(params?.body),
    }),
  },
  getLeadListings: {
    query: (queryParams) => {
      const { leadId, filtersList, params } = queryParams;
      const mappedParams = mapQueryParams(params, filtersList);
      return `/api/lms/leads/${leadId}/listings?${mappedParams}`;
    },
    transformer: (response) => {
      return response?.listings?.map((item) => tenantTransformers.listingDetailMapper(item));
    },
  },
  getLeadDetail: {
    query: (queryParams) => {
      const { leadId } = queryParams;
      return `/api/lms/leads/${leadId}`;
    },
    transformer: (response) => tenantTransformers.leadDetailMapper(response?.leads),
  },
  getLeadInterests: {
    query: (queryParams) => {
      const { leadId, queryObj } = queryParams;
      return `/api/lms/interests?q[lead_id_eq]=${leadId}&page=${queryObj?.page || 1}`;
    },
    transformer: (response) => tenantTransformers.leadInterestsMapper(response),
  },
  getTasks: {
    query: (queryParams) => {
      const { leadId, queryObj } = queryParams;
      return `/api/lms/tasks?q[taskable_id_eq]=${leadId}&page=${queryObj?.page || 1}`;
    },
    transformer: (response) => tenantTransformers.tasksMapper(response),
  },

  getUserActiveListings: {
    query: (queryParams) => {
      const { filtersList, params } = queryParams;
      const mappedParams = mapQueryParams(params, filtersList);
      return `/api/lms/listings?${mappedParams}`;
    },
    transformer: (response) => {
      return response?.listings?.map((item) => tenantTransformers.listingDetailMapper(item));
    },
  },
  getLeadCallRecording: {
    query: (queryParams) => {
      const { recording_uuid } = queryParams;
      return {
        url: `/api/lms/recordings/call?id=${recording_uuid}`,
        method: 'GET',
        responseHandler: async (response) => {
          if (!response.ok) {
            throw new Error(response.error);
          }
          const blob = await response.blob();
          const audioUrl = URL.createObjectURL(blob);
          return audioUrl;
        },
      };
    },
  },
  markLeadAsViewed: {
    query: ({ leadId }) => ({
      url: `/api/lms/leads/${leadId}/mark_as_viewed`,
      method: 'PUT',
    }),
  },

  getProductStats: {
    queryFn: async (queryParams, api, extraOptions, baseQuery) => {
      const { selectedUser, params } = queryParams || {};
      const leadsStats = await baseQuery(
        `/api/lms/stats/product_stats?${tenantUtils.getInitialPayload({ ...params, group_by: ['date', 'ad_product'], user: selectedUser })}`,
      );
      let phoneStats;
      const { start_date, end_date } = tenantUtils.getInitialPayload({ ...params }, true);
      const dateArray = getDateLabels(start_date, end_date, 'YYYY-MM-DD');
      if (
        !leadsStats?.error &&
        selectedUser?.is_lms_enabled &&
        selectedUser?.is_call_tracking_enabled &&
        tenantConstants.IS_LMS_ENABLED
      ) {
        const phoneStatsResponse = await baseQuery(
          `/api/lms/stats/phone_lead_stats?${tenantUtils.getInitialPayload({ ...params, group_by: ['date', 'ad_product'], user: selectedUser }, false, ['start_datetime', 'end_datetime'])}`,
        );
        phoneStats = phoneStatsResponse?.error
          ? null
          : tenantTransformers.phoneStatsMapper(phoneStatsResponse?.data?.stats, dateArray);
      }
      if (leadsStats) {
        if (leadsStats.error) {
          return leadsStats;
        } else {
          return { data: tenantTransformers.productStatsMapper(leadsStats, selectedUser, phoneStats, dateArray) };
        }
      }
    },
  },
  addLead: {
    query: (body) => ({
      url: `/api/lms/leads`,
      method: 'POST',
      body: tenantPayloads.addLeadPayload(body),
    }),
  },
  getZiwoAICallTrackingData: {
    query: (interestId) => `/api/lms/interests/${interestId}`,
    transformer: (response) => response?.interest,
  },
};

export default LeadsEndpoints;
